'use client';

import React from 'react';
import { useSession, signOut } from 'next-auth/react';
import Link from 'next/link';
import {
  PiUserCircleGear,
  PiChartLineUpLight,
  PiSignOutLight,
} from 'react-icons/pi';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSection,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import Avatar from '@/components/Account/Avatar';
import { useGlobalAvatar } from '@/hooks/useGlobalAvatar';
import { observer } from 'mobx-react-lite';

type UserMenuProps = {
  manageAccountLabel?: string;
  myProgressLabel?: string;
  signOutLabel?: string;
  accountHref?: string;
  progressHref?: string;
};

export const UserMenu = observer(({
  manageAccountLabel = 'Manage account',
  myProgressLabel = 'My progress',
  signOutLabel = 'Sign out',
  accountHref = '/account/profile',
  progressHref = '/account/profile',
}: UserMenuProps) => {
  const session = useSession();
  const { avatarUrl, displayName: apiDisplayName, loading: avatarLoading } = useGlobalAvatar();

  if (!session?.data?.user) {
    return null;
  }

  // Use displayName from API for consistency with account page (affects MusicNote icon generation)
  const displayName = apiDisplayName || session?.data?.user?.displayName;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className='cursor-pointer rounded-full'>
          <Avatar
            src={avatarUrl}
            name={displayName}
            loading={avatarLoading && !!avatarUrl}
            size='sm'
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent showOverlay={false} align='end' className='w-[250px]'>
        <DropdownMenuSection>
          <DropdownMenuItem asChild icon={<PiUserCircleGear size={20} />}>
            <Link href={accountHref}>
              {manageAccountLabel}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild icon={<PiChartLineUpLight size={20} />}>
            <Link href={progressHref}>
              {myProgressLabel}
            </Link>
          </DropdownMenuItem>
        </DropdownMenuSection>
        <DropdownMenuSeparator />
        <DropdownMenuSection>
          <DropdownMenuItem
            onClick={() => signOut()}
            className='cursor-pointer'
            icon={<PiSignOutLight size={20} />}
          >
            {signOutLabel}
          </DropdownMenuItem>
        </DropdownMenuSection>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
